import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LOCALES,
  MIN_PASSWORD_LENGTH,
  ROLES,
  type AdminUser,
  type Locale,
  type Role,
} from '@competa/shared';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { TextField } from '../../components/TextField';
import { errorMessage, fieldErrors } from '../../i18n/errors';
import styles from './Admin.module.css';

/** Daten des Formulars (alle Felder als Text, wie sie im Browser vorliegen). */
export interface UserFormValues {
  username: string;
  password: string;
  role: Role;
  locale: Locale;
  /** Datum im Format `YYYY-MM-DD` oder leer (= unbefristet); nur beim Bearbeiten. */
  expiresOn: string;
}

/** Eigenschaften des Benutzerdialogs. */
interface UserDialogProps {
  /** Zu bearbeitender Benutzer; `null` legt einen neuen an. */
  user: AdminUser | null;
  /** Ob der Dialog geöffnet ist. */
  isOpen: boolean;
  /** Schließt den Dialog ohne Speichern. */
  onClose: () => void;
  /** Speichert die Eingaben; wirft bei Fehlern (`ApiRequestError`), die der Dialog anzeigt. */
  onSubmit: (values: UserFormValues) => Promise<void>;
}

/**
 * Wandelt ein ISO-Datum in das Format `YYYY-MM-DD` für `<input type="date">` um.
 * @param iso ISO-8601-Zeitpunkt oder `null`.
 * @returns Datumstext oder leerer String.
 */
export function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/**
 * Dialog zum Anlegen und Bearbeiten eines Benutzers. Zeigt Feldfehler des Servers am Feld an.
 * Beim Bearbeiten bedeutet ein leeres Passwortfeld „unverändert".
 * @param props Benutzer, Zustand und Callbacks.
 */
export function UserDialog({ user, isOpen, onClose, onSubmit }: UserDialogProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<UserFormValues>({
    username: user?.username ?? '',
    password: '',
    role: user?.role ?? 'user',
    locale: user?.locale ?? 'de',
    expiresOn: toDateInput(user?.expiresAt ?? null),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const editing = user !== null;

  /**
   * Setzt ein Formularfeld.
   * @param field Feldname.
   * @param value Neuer Wert.
   */
  function update<K extends keyof UserFormValues>(field: K, value: UserFormValues[K]): void {
    setValues((current) => ({ ...current, [field]: value }));
  }

  /**
   * Sendet das Formular und zeigt Fehler an.
   * @param event Absenden des Formulars.
   */
  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors({});
    setGeneralError(null);
    setBusy(true);
    try {
      await onSubmit(values);
    } catch (caught) {
      const fields = fieldErrors(t, caught);
      setErrors(fields);
      setGeneralError(Object.keys(fields).length === 0 ? errorMessage(t, caught) : null);
    } finally {
      setBusy(false);
    }
  }

  const title = editing
    ? t('admin.users.editTitle', { name: user.username })
    : t('admin.users.createTitle');
  return (
    <Dialog title={title} isOpen={isOpen} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.formGrid}>
          <TextField
            label={t('admin.users.name')}
            value={values.username}
            onChange={(event) => update('username', event.target.value)}
            error={errors.username}
            autoComplete="off"
            required
          />
          <TextField
            label={editing ? t('admin.users.newPassword') : t('admin.users.initialPassword')}
            type="password"
            value={values.password}
            onChange={(event) => update('password', event.target.value)}
            hint={t('password.hint', { min: MIN_PASSWORD_LENGTH })}
            error={errors.password}
            autoComplete="new-password"
            required={!editing}
          />
          <div className={styles.selectField}>
            <label htmlFor="user-role">{t('admin.users.role')}</label>
            <select
              id="user-role"
              className={styles.select}
              value={values.role}
              onChange={(event) => update('role', event.target.value as Role)}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role === 'admin' ? t('auth.roleAdmin') : t('auth.roleUser')}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.selectField}>
            <label htmlFor="user-locale">{t('admin.users.locale')}</label>
            <select
              id="user-locale"
              className={styles.select}
              value={values.locale}
              onChange={(event) => update('locale', event.target.value as Locale)}
            >
              {LOCALES.map((locale) => (
                <option key={locale} value={locale}>
                  {t(`lang.${locale}`)}
                </option>
              ))}
            </select>
          </div>
          {editing && (
            <TextField
              label={t('admin.users.expires')}
              type="date"
              value={values.expiresOn}
              onChange={(event) => update('expiresOn', event.target.value)}
              hint={t('common.unlimited')}
            />
          )}
        </div>
        <p className={styles.note}>{t('admin.users.passwordResetNote')}</p>
        {generalError && (
          <p role="alert" className={styles.error}>
            {generalError}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" disabled={busy}>
            {editing ? t('common.save') : t('common.create')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
